import React, { useState, useRef, useEffect, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { X, ImagePlus, Loader2, RotateCw, Star, GripVertical, AlertTriangle } from "lucide-react";
import { toast } from "@/lib/toast";

const MAX = 10;
const MAX_BYTES = 10 * 1024 * 1024;
const IMG_EXT = /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i;
const UPLOAD_TIMEOUT = 45000;

const isImageFile = (f) => (f.type && f.type.startsWith("image/")) || IMG_EXT.test(f.name || "");

let _seq = 0;
const nextKey = () => `pf_${Date.now()}_${_seq++}`;

// Listing photo uploader: full-area drag & drop, clipboard paste, per-file upload
// with progress/timeout/retry, and touch-first drag-to-reorder. `value` is the
// URL array that gets submitted (index 0 = cover); reorder mutates it directly.
export const ListingPhotoUploader = ({ value = [], onChange }) => {
  // pending = uploads in flight or failed (no URL yet). Completed photos live in `value`.
  const [pending, setPending] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const dragDepth = useRef(0);
  const fileRef = useRef();
  const dragRef = useRef(null);
  const valueRef = useRef(value);
  const pendingRef = useRef(pending);

  useEffect(() => { valueRef.current = value; }, [value]);
  useEffect(() => { pendingRef.current = pending; }, [pending]);

  const total = value.length + pending.length;

  // Update the ref synchronously so concurrent upload completions don't race on a stale array.
  const commit = useCallback((urls) => { valueRef.current = urls; onChange(urls); }, [onChange]);

  const uploadOne = useCallback((entry) => {
    const fd = new FormData();
    fd.append("file", entry.file);
    api.post("/listings/upload-photo", fd, {
      timeout: UPLOAD_TIMEOUT,
      onUploadProgress: (e) => {
        if (!e.total) return;
        const p = Math.round((e.loaded / e.total) * 100);
        setPending((cur) => cur.map((x) => (x.key === entry.key ? { ...x, progress: p } : x)));
      },
    })
      .then(({ data }) => {
        const next = [...(valueRef.current || []), data.url].slice(0, MAX);
        valueRef.current = next;
        onChange(next);
        setPending((cur) => cur.filter((x) => x.key !== entry.key));
      })
      .catch((err) => {
        const msg = err.code === "ECONNABORTED"
          ? "Upload timed out"
          : (formatError(err.response?.data?.detail) || "Upload failed");
        setPending((cur) => cur.map((x) => (x.key === entry.key ? { ...x, status: "failed", error: msg } : x)));
      });
  }, [onChange]);

  const addFiles = useCallback((fileList) => {
    const all = Array.from(fileList || []);
    if (!all.length) return;

    const skipped = [];
    const valid = [];
    for (const f of all) {
      if (!isImageFile(f)) { skipped.push({ name: f.name, reason: "not an image" }); continue; }
      if (f.size > MAX_BYTES) { skipped.push({ name: f.name, reason: "over 10 MB" }); continue; }
      valid.push(f);
    }

    const used = valueRef.current.length + pendingRef.current.length;
    const room = Math.max(0, MAX - used);
    const accepted = valid.slice(0, room);
    const overflow = valid.length - accepted.length;

    if (skipped.length) {
      const head = skipped.slice(0, 3).map((s) => `${s.name || "file"} (${s.reason})`).join(", ");
      toast.error(`${skipped.length} file${skipped.length > 1 ? "s" : ""} skipped: ${head}${skipped.length > 3 ? "…" : ""}`);
    }
    if (overflow > 0) {
      toast.error(`Photo limit is ${MAX}. ${overflow} photo${overflow > 1 ? "s were" : " was"} not added.`);
    }
    if (!accepted.length) return;

    const entries = accepted.map((file) => ({ key: nextKey(), name: file.name, status: "uploading", progress: 0, file }));
    setPending((cur) => [...cur, ...entries]);
    entries.forEach(uploadOne);
  }, [uploadOne]);

  const retry = (key) => {
    const entry = pendingRef.current.find((x) => x.key === key);
    if (!entry) return;
    setPending((cur) => cur.map((x) => (x.key === key ? { ...x, status: "uploading", progress: 0, error: null } : x)));
    uploadOne(entry);
  };

  const removePending = (key) => setPending((cur) => cur.filter((x) => x.key !== key));
  const removeDone = (i) => commit(valueRef.current.filter((_, idx) => idx !== i));
  const makeCover = (i) => {
    if (i === 0) return;
    const arr = [...valueRef.current];
    const [m] = arr.splice(i, 1);
    arr.unshift(m);
    commit(arr);
  };

  // ---- Pointer-based reorder (mouse + touch) over completed tiles only ----
  const pointerDown = (i) => (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragRef.current = i;
    setDragIdx(i);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const pointerMove = (e) => {
    if (dragRef.current == null) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const tile = el && el.closest("[data-done-index]");
    if (tile) {
      const idx = Number(tile.getAttribute("data-done-index"));
      if (!Number.isNaN(idx)) setOverIdx(idx);
    }
  };
  const pointerUp = () => {
    const from = dragRef.current;
    const to = overIdx;
    dragRef.current = null;
    setDragIdx(null);
    setOverIdx(null);
    if (from == null || to == null || from === to) return;
    const arr = [...valueRef.current];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    commit(arr);
  };

  // ---- Full-area drop zone ----
  const onDragEnter = (e) => { e.preventDefault(); dragDepth.current += 1; setDragOver(true); };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDragLeave = (e) => { e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragOver(false); } };
  const onDrop = (e) => { e.preventDefault(); dragDepth.current = 0; setDragOver(false); addFiles(e.dataTransfer?.files); };

  // ---- Clipboard paste ----
  useEffect(() => {
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.files || []).filter(isImageFile);
      if (files.length) { e.preventDefault(); addFiles(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [addFiles]);

  return (
    <div
      data-testid="photo-dropzone"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className="relative"
    >
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
        {value.map((url, i) => (
          <div
            key={url}
            data-done-index={i}
            data-testid={`photo-thumb-${i}`}
            onPointerDown={pointerDown(i)}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onPointerCancel={pointerUp}
            className={`relative aspect-square border select-none touch-none cursor-grab active:cursor-grabbing group
              ${i === 0 ? "border-olive-bright" : "border-sniper"}
              ${dragIdx === i ? "opacity-40 pointer-events-none" : ""}
              ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-[#748A3D]" : ""}`}
          >
            <img src={url} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" />
            {i === 0 ? (
              <span data-testid="photo-cover-label" className="absolute bottom-1 left-1 bg-olive-bright text-black text-[8px] font-mono uppercase px-1 py-0.5 tracking-wide">Cover</span>
            ) : (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => makeCover(i)}
                data-testid={`set-primary-${i}`}
                title="Make cover photo"
                className="absolute bottom-1 left-1 bg-black/80 text-olive-bright p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Star className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => removeDone(i)}
              data-testid={`remove-photo-${i}`}
              className="absolute top-1 right-1 bg-black/80 text-blaze p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
            <span className="absolute top-1 left-1 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <GripVertical className="w-4 h-4" strokeWidth={1.6} />
            </span>
          </div>
        ))}

        {pending.map((p) => (
          <div
            key={p.key}
            data-testid={p.status === "failed" ? `photo-tile-failed-${p.key}` : `photo-tile-uploading-${p.key}`}
            className="relative aspect-square border border-sniper bg-surface flex flex-col items-center justify-center overflow-hidden"
          >
            {p.status === "uploading" ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin text-olive-bright" strokeWidth={1.4} />
                <span className="text-[9px] font-mono uppercase mt-1 text-muted-foreground">{p.progress || 0}%</span>
                <div className="absolute bottom-0 left-0 h-0.5 bg-olive-bright transition-all" style={{ width: `${p.progress || 0}%` }} />
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-blaze" />
                <span className="text-[8px] font-mono uppercase mt-1 text-blaze px-1 text-center leading-tight">{p.error || "Failed"}</span>
                <div className="flex items-center gap-1 mt-1.5">
                  <button
                    type="button"
                    onClick={() => retry(p.key)}
                    data-testid={`photo-retry-${p.key}`}
                    className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase text-olive-bright border border-olive/50 hover:border-olive-bright px-1.5 py-0.5 transition-colors"
                  >
                    <RotateCw className="w-3 h-3" /> Retry
                  </button>
                  <button
                    type="button"
                    onClick={() => removePending(p.key)}
                    data-testid={`photo-remove-pending-${p.key}`}
                    className="text-blaze hover:text-blaze/80"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {total < MAX && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="add-photo-btn"
            className={`aspect-square border border-dashed flex flex-col items-center justify-center transition-colors
              ${dragOver ? "border-[#748A3D] border-solid text-[#748A3D] bg-olive/10" : "border-sniper text-muted-foreground hover:border-olive hover:text-olive-bright"}`}
          >
            <ImagePlus className="w-6 h-6" strokeWidth={1.4} />
            <span className="text-[10px] font-mono uppercase mt-1">Add</span>
          </button>
        )}
      </div>

      {dragOver && (
        <div data-testid="photo-drop-overlay" className="absolute inset-0 flex items-center justify-center pointer-events-none bg-obsidian/70 border-2 border-dashed border-[#748A3D]">
          <span className="font-heading uppercase tracking-wide text-[#748A3D] text-lg">Drop to add</span>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        hidden
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        data-testid="photo-input"
      />
    </div>
  );
};

export default ListingPhotoUploader;
